import { Alert, Box, useToast } from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState } from 'react';

import { Util } from '@finley-technologies/finley-types';

import {
  BorrowingBasePipelineSingleProgressBreakdownGQLType,
  DownloadWithLoanProductGQLType,
  UrlAndFilenameGQLType,
  VerificationPipelineStatusGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import {
  useGetFundingRequestVerificationReportByMetadataQuery,
  useGetLenderVerificationProgressBreakdownQuery,
} from '../requests/requests.gql.generated';
import { VerificationApproveSection } from './VerificationApproveSection';
import { VerificationsTemplateV2 } from './VerificationsTemplate';
import { VerificationPipelineRunId } from './VerificationTableSharedComponents';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';

export const VerificationWorkflowWithPipeline = ({
  organizationId,
  pipelineRunId,
}: {
  organizationId: string;
  pipelineRunId: VerificationPipelineRunId;
}) => {
  const toast = useToast();
  const creditFacilityId = pipelineRunId.creditFacilityId;
  const selectedReportingDate = pipelineRunId.reportingDate;
  const artifactDocumentId = pipelineRunId.artifactDocumentId;
  const fileVersion = pipelineRunId.fileVersion;

  const [
    verificationStatus,
    setVerificationStatus,
  ] = useState<VerificationPipelineStatusGQLType | null>(null);

  const [
    verificationReport,
    setVerificationReport,
  ] = useState<Util.SanitizedJSON | null>(null);
  const [verificationReportId, setVerificationReportId] = useState<
    string | null
  >(null);
  const [
    verificationReportBorrowingBaseReportId,
    setVerificationReportBorrowingBaseReportId,
  ] = useState<string | null>(null);
  const [remediationStatus, setRemediationStatus] = useState<{
    remediationRequested: boolean;
    remediationNote: string | null;
  } | null>(null);

  const [progressBreakdownStatus, setProgressBreakdownStatus] = useState<
    BorrowingBasePipelineSingleProgressBreakdownGQLType[]
  >([]);
  const [
    uploadProgressBreakdownStatus,
    setUploadProgressBreakdownStatus,
  ] = useState<BorrowingBasePipelineSingleProgressBreakdownGQLType[]>([]);

  const [
    servicerReportDownload,
    setServicerReportDownload,
  ] = useState<UrlAndFilenameGQLType | null>(null);
  const [
    extendedResponseTapeDownloads,
    setExtendedResponseTapeDownloads,
  ] = useState<DownloadWithLoanProductGQLType[] | null>(null);

  useGetFundingRequestVerificationReportByMetadataQuery({
    variables: {
      artifactDocumentId,
      creditFacilityId,
      reportingDate: pipelineRunId.reportingDate,
      fileVersion: pipelineRunId.fileVersion,
    },
    onCompleted: (data) => {
      if (data && data.fundingRequestVerificationReportByMetadata) {
        const parsedVerificationReport: Util.SanitizedJSON = JSON.parse(
          data.fundingRequestVerificationReportByMetadata.verificationReport,
        );
        setVerificationReport(parsedVerificationReport);
        setVerificationReportId(
          data.fundingRequestVerificationReportByMetadata.verificationReportId,
        );
        setVerificationReportBorrowingBaseReportId(
          data.fundingRequestVerificationReportByMetadata.borrowingBaseReportId,
        );
        setRemediationStatus({
          remediationRequested:
            data.fundingRequestVerificationReportByMetadata
              .remediationRequested ?? false,
          remediationNote:
            data.fundingRequestVerificationReportByMetadata.remediationNote ??
            null,
        });
      }
    },
  });

  useGetLenderVerificationProgressBreakdownQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      artifactDocumentId,
      creditFacilityId,
      reportingDate: selectedReportingDate,
      fileVersion,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data.lenderVerificationProgressBreakdown) {
        const progressBreakdownData = data.lenderVerificationProgressBreakdown;
        const uploadProgressStepsData =
          progressBreakdownData.uploadProgressBreakdown;
        const progressStepsData = progressBreakdownData.progressBreakdown;

        setVerificationStatus(progressBreakdownData.status);

        if (!_.isEqual(progressBreakdownStatus, progressStepsData)) {
          setProgressBreakdownStatus(progressStepsData);
        }

        if (
          !_.isEqual(uploadProgressBreakdownStatus, uploadProgressStepsData)
        ) {
          setUploadProgressBreakdownStatus(uploadProgressStepsData);
        }

        const newServicerReportDownload =
          progressBreakdownData.servicerReportDownload ?? null;
        if (!_.isEqual(servicerReportDownload, newServicerReportDownload)) {
          setServicerReportDownload(newServicerReportDownload);
        }

        const newEtrDownload =
          progressBreakdownData.extendedResponseTapeDownloads ?? null;
        if (!_.isEqual(extendedResponseTapeDownloads, newEtrDownload)) {
          setExtendedResponseTapeDownloads(newEtrDownload);
        }
      }
    },
  });

  return (
    <VerificationsTemplateV2
      status={verificationStatus}
      selectedCreditFacilityId={creditFacilityId}
      selectedReportingDate={selectedReportingDate}
      selectedFileVersion={fileVersion}
    >
      <Box height="32px" />
      {(() => {
        if (
          !verificationReport ||
          !verificationReportId ||
          !verificationReportBorrowingBaseReportId
        ) {
          return (
            <InfoAlertWithDescription description="Please wait until calculations are complete." />
          );
        }

        return (
          <VerificationApproveSection
            organizationId={organizationId}
            selectedCreditFacilityId={creditFacilityId}
            selectedReportingDate={selectedReportingDate}
            selectedFileVersion={fileVersion}
            verificationReport={verificationReport}
            verificationReportId={verificationReportId}
            verificationReportBorrowingBaseReportId={
              verificationReportBorrowingBaseReportId
            }
            artifactDocumentId={artifactDocumentId}
            canApproveOrDeny={verificationStatus === 'awaitingApproval'}
            extendedResponseTapeDownloads={extendedResponseTapeDownloads}
            servicerReportDownload={servicerReportDownload}
            remediationStatus={remediationStatus}
          />
        );
      })()}
    </VerificationsTemplateV2>
  );
};

export const VerificationWorkflow = ({
  organizationId,
  pipelineRunId,
}: {
  organizationId: string;
  pipelineRunId?: VerificationPipelineRunId;
}) => {
  if (!pipelineRunId) {
    // this should never happen for v2. remove this.
    return (
      <Alert status="error">
        Chose upload button to create new verification.
      </Alert>
    );
  }

  return (
    <VerificationWorkflowWithPipeline
      organizationId={organizationId}
      pipelineRunId={pipelineRunId}
    />
  );
};
